'use client'

import Link from 'next/link'
import Image from 'next/image'
import logo from '../assets/crypto-street-logo.svg'
import { useState, useEffect, useRef } from 'react'
import { Search } from 'lucide-react'

// ─── Nav data ─────────────────────────────────────────────────────────────────

type NavItem = {
  label: string
  href?: string
  desc?: string
  icon?: React.ReactNode
  soon?: boolean
  external?: boolean
  children?: { label: string; desc: string; href: string }[]
}

type NavGroup = {
  title: string
  items: NavItem[]
}

const NAV: { label: string; href?: string; groups?: NavGroup[] }[] = [
  { label: 'News', href: '/news' },
  {
    label: 'Prices',
    href: '/prices',
    groups: [
      {
        title: 'Markets',
        items: [
          {
            label: 'All prices',
            desc: 'Live prices for 100+ cryptocurrencies',
            href: '/prices',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            ),
          },
          {
            label: 'Heatmap',
            desc: 'Visual market snapshot by sector',
            href: '/heatmap',
            icon: (
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                <rect x="1" y="1" width="6" height="6" rx="1" fill="currentColor" opacity="0.7" />
                <rect x="9" y="1" width="6" height="3" rx="1" fill="currentColor" opacity="0.5" />
                <rect x="9" y="5.5" width="6" height="2.5" rx="1" fill="currentColor" opacity="0.35" />
                <rect x="1" y="9" width="4" height="6" rx="1" fill="currentColor" opacity="0.4" />
                <rect x="6.5" y="9" width="4" height="6" rx="1" fill="currentColor" opacity="0.28" />
                <rect x="12" y="9" width="3" height="6" rx="1" fill="currentColor" opacity="0.2" />
              </svg>
            ),
          },
          {
            label: 'Top gainers',
            desc: 'Best performing coins today',
            href: '/prices/gainers',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
                <polyline points="18 15 12 9 6 15" />
              </svg>
            ),
          },
          {
            label: 'Top losers',
            desc: 'Worst performing coins today',
            href: '/prices/losers',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            ),
          },
        ],
      },
    ],
  },
  {
    label: 'Tools',
    groups: [
      {
        title: '',
        items: [
          {
            label: 'Converter',
            desc: 'Live rates across 16 currencies',
            href: '/prices/bitcoin/converter',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M7 16V4m0 0L3 8m4-4l4 4" /><path d="M17 8v12m0 0l4-4m-4 4l-4-4" />
              </svg>
            ),
          },
          {
            label: 'Predictions',
            desc: 'Technical analysis & price forecasts',
            href: '/prices/bitcoin/prediction',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            ),
          },
          {
            label: 'Price history',
            desc: 'Daily tables & monthly heatmaps',
            href: '/prices/bitcoin/history',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 15" />
              </svg>
            ),
          },
          {
            label: 'Exchanges',
            desc: 'Best venues to trade each coin',
            href: '/prices/bitcoin/exchanges',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 6h18M3 12h18M3 18h18" />
                <circle cx="19" cy="6" r="2" fill="currentColor" stroke="none" opacity="0.4" />
                <circle cx="5" cy="12" r="2" fill="currentColor" stroke="none" opacity="0.4" />
                <circle cx="19" cy="18" r="2" fill="currentColor" stroke="none" opacity="0.4" />
              </svg>
            ),
          },
        ],
      },
    ],
  },
  {
    label: 'Dominance',
    href: '/dominance',
    groups: [
      {
        title: '',
        items: [
          {
            label: 'Market dominance',
            desc: 'BTC, ETH & altcoin market share',
            href: '/dominance',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" />
              </svg>
            ),
          },
          {
            label: 'Bitcoin dominance',
            desc: 'BTC dominance charts & analysis',
            href: '/dominance/bitcoin',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <path d="M9 8v8M9 8h3.5a2.5 2.5 0 0 1 0 5H9m0 0h4a2.5 2.5 0 0 1 0 5H9" />
              </svg>
            ),
          },
        ],
      },
    ],
  },
  {
    label: 'Exchanges',
    href: '/exchanges',
    groups: [
      {
        title: '',
        items: [
          {
            label: 'All exchanges',
            desc: 'Compare CEX & DEX',
            href: '/exchanges',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 8h14M3 8l4-4M3 8l4 4" />
              <path d="M21 16H7m14 0l-4-4m4 4l-4 4" />
            </svg>
          
            ),
          },
          {
            label: 'Bitget',
            desc: 'Overview, fees & referral bonus',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 12h8M12 8v8" />
              </svg>
            ),
            children: [
              { label: 'Overview', desc: 'Volume, pairs & trust score', href: '/exchanges/bitget' },
              { label: 'Fees', desc: 'Maker, taker & cost calculator', href: '/exchanges/bitget/fees' },
            ],
          },
          {
            label: 'BingX',
            desc: 'Overview, fees & referral bonus',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 12h8M12 8v8" />
              </svg>
            ),
            children: [
              { label: 'Overview', desc: 'Volume, pairs & trust score', href: '/exchanges/bingx' },
              { label: 'Fees', desc: 'Maker, taker & cost calculator', href: '/exchanges/bingx/fees' },
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'Prop firm',
    href: '/prop-firm',
    groups: [
      {
        title: '',
        items: [
          {
            label: 'Overview',
            desc: 'Challenges, rules & payout structure',
            href: '/prop-firm',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            ),
          },
          {
            label: 'Start a challenge',
            desc: 'Trade our $200k, not yours',
            href: '/prop-firm#sizes',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.5">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
            ),
          },
          {
            label: 'Trader login',
            desc: 'Access your funded account dashboard',
            href: 'https://trade.crypto.st/en/sign-in',
            external: true,
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
            ),
          },
        ],
      },
    ],
  },
  {
    label: 'More',
    groups: [
      {
        title: 'Resources',
        items: [
          {
            label: 'Crypto terms',
            desc: 'Glossary of key crypto concepts',
            href: '/terms',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            ),
          },
          {
            label: 'Jobs',
            desc: 'Crypto & Web3 job listings',
            href: '/jobs',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
              </svg>
            ),
          },
          {
            label: 'Learn',
            desc: 'Guides & educational content',
            href: '/#',
            soon: true,
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5c3 3 9 3 12 0v-5" />
              </svg>
            ),
          },
          {
            label: 'Reviews',
            desc: 'Exchange & product reviews',
            href: '/#',
            soon: true,
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            ),
          },
        ],
      },
    ],
  },
]

// ─── Chevron ──────────────────────────────────────────────────────────────────

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="10" height="10" viewBox="0 0 10 10" fill="none"
      className={`ml-0.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    >
      <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ─── Desktop dropdown ─────────────────────────────────────────────────────────

function Dropdown({ groups }: { groups: NavGroup[] }) {
  const [flyout, setFlyout] = useState<string | null>(null)

  return (
    <div className="absolute top-full left-0 mt-3 bg-zinc-50 border border-zinc-200/80 shadow-[0_8px_30px_rgba(0,0,0,0.08)] z-50 min-w-[280px]">
      {groups.map((group, gi) => (
        <div key={gi} className={`py-3 ${gi < groups.length - 1 ? 'border-b border-zinc-100' : ''}`}>
          {group.title && (
            <div className="px-4 pb-2 pt-1">
              <span className="text-zinc-500 text-[9px] uppercase tracking-widest font-semibold font-[family-name:var(--font-saira)]">
                {group.title}
              </span>
            </div>
          )}
          {group.items.map((item) =>
            item.children ? (
              <div
                key={item.label}
                onMouseEnter={() => setFlyout(item.label)}
                onMouseLeave={() => setFlyout(null)}
                className="relative flex items-start gap-3 px-4 py-2.5 hover:bg-zinc-100 transition-colors group cursor-default"
              >
                <div className="text-zinc-400 group-hover:text-zinc-600 transition-colors mt-0.5 flex-shrink-0">
                  {item.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-black text-sm font-semibold font-[family-name:var(--font-saira)] whitespace-nowrap">
                      {item.label}
                    </span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-400 ml-4 flex-shrink-0">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                  {item.desc && (
                    <div className="text-zinc-500 text-xs mt-0.5 font-[family-name:var(--font-saira)] leading-snug">
                      {item.desc}
                    </div>
                  )}
                </div>
                {flyout === item.label && (
                  <div className="absolute left-full top-0 bg-zinc-50 border border-zinc-200/80 shadow-[0_8px_30px_rgba(0,0,0,0.08)] min-w-[200px] py-2 z-50">
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        href={child.href}
                        className="flex flex-col px-4 py-2.5 hover:bg-zinc-100 transition-colors"
                      >
                        <span className="text-black text-sm font-semibold font-[family-name:var(--font-saira)]">
                          {child.label}
                        </span>
                        <span className="text-zinc-500 text-xs mt-0.5 font-[family-name:var(--font-saira)]">
                          {child.desc}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.href}
                href={item.href ?? '#'}
                {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="flex items-start gap-3 px-4 py-2.5 hover:bg-zinc-100 transition-colors group"
              >
                <div className="text-zinc-400 group-hover:text-zinc-600 transition-colors mt-0.5 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-black text-sm font-semibold font-[family-name:var(--font-saira)] group-hover:text-black whitespace-nowrap">
                      {item.label}
                    </span>
                    {item.soon && (
                      <span className="bg-zinc-100 text-zinc-400 text-[8px] uppercase tracking-widest px-1.5 py-px font-semibold font-[family-name:var(--font-saira)]">
                        Soon
                      </span>
                    )}
                    {item.external && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-300 flex-shrink-0">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    )}
                  </div>
                  <div className="text-zinc-700 text-xs mt-0.5 font-[family-name:var(--font-saira)] leading-snug">
                    {item.desc}
                  </div>
                </div>
              </Link>
            )
          )}
        </div>
      ))}
    </div>
  )
}

// ─── Header ───────────────────────────────────────────────────────────────────

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null)
  const [mobileSubExpanded, setMobileSubExpanded] = useState<string | null>(null)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMobileOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function enterDropdown(label: string) {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setActiveDropdown(label)
  }

  function leaveDropdown() {
    timeoutRef.current = setTimeout(() => setActiveDropdown(null), 130)
  }

  return (
    <div ref={menuRef}>
      <header className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 h-12 lg:h-20 flex items-center justify-between">

          {/* Logo */}
          <Link href="/">
            <Image
              src={logo}
              alt="crypto.st"
              height={32}
              width={120}
              className="lg:w-[180px] lg:h-[64px]"
              priority
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6 text-sm ml-12">
            {NAV.map((item) => {
              const hasDropdown = !!item.groups
              const isOpen = activeDropdown === item.label
              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => hasDropdown && enterDropdown(item.label)}
                  onMouseLeave={() => hasDropdown && leaveDropdown()}
                >
                  <Link
                    href={item.href ?? '#'}
                    onClick={(e) => { if (hasDropdown && !item.href) e.preventDefault() }}
                    className={`flex items-center gap-1.5 transition-colors font-[family-name:var(--font-saira)] whitespace-nowrap ${
                      isOpen ? 'text-green-500 font-bold' : 'text-gray-100 hover:text-green-500 hover:font-bold'
                    }`}
                  >
                    {item.label}
                    {hasDropdown && <Chevron open={isOpen} />}
                  </Link>
                  {hasDropdown && isOpen && <Dropdown groups={item.groups!} />}
                </div>
              )
            })}
          </nav>

          {/* Right side — desktop */}
          <div className="hidden lg:flex items-center gap-4 ml-auto">
            <Link
              href="/search"
              aria-label="Search"
              className="text-zinc-400 hover:text-white transition-colors"
            >
              <Search size={20} strokeWidth={1.5} />
            </Link>
            <Link
              href="/prop-firm"
              className="border border-green-500 text-green-500 hover:bg-green-500 hover:text-black text-[11px] font-bold uppercase tracking-widest px-4 py-2 transition-all font-[family-name:var(--font-saira)] whitespace-nowrap"
            >
              Start trading
            </Link>
            <Link
              href="https://trade.crypto.st/en/sign-in"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-white text-[9px] uppercase tracking-widest font-bold font-[family-name:var(--font-saira)] transition-colors leading-tight"
            >
              Trader<br />Login
            </Link>
          </div>

          {/* Mobile right side */}
          <div className="flex items-center gap-2 lg:hidden">
            <Link
              href="/search"
              aria-label="Search"
              className="text-zinc-400 hover:text-white transition-colors p-2"
            >
              <Search size={20} strokeWidth={1.5} />
            </Link>
            <button
              className="flex flex-col gap-1.5 p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
              <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <nav className="lg:hidden bg-black flex flex-col gap-0">
            {NAV.map((item) => {
              const hasDropdown = !!item.groups
              const isExpanded = mobileExpanded === item.label
              return (
                <div key={item.label} className="border-b border-zinc-900">
                  {hasDropdown ? (
                    <>
                      <button
                        onClick={() => setMobileExpanded(isExpanded ? null : item.label)}
                        className="w-full flex items-center justify-between px-8 py-3"
                      >
                        <span className={`text-lg font-[family-name:var(--font-saira)] ${isExpanded ? 'text-green-500 font-bold' : 'text-gray-100'}`}>
                          {item.label}
                        </span>
                        <Chevron open={isExpanded} />
                      </button>
                      {isExpanded && (
                        <div className="bg-zinc-950 pb-2">
                          {item.groups!.map((group) => (
                            <div key={group.title}>
                              {group.title && (
                                <div className="px-8 pt-3 pb-1">
                                  <span className="text-zinc-400 text-[9px] uppercase tracking-widest font-semibold font-[family-name:var(--font-saira)]">
                                    {group.title}
                                  </span>
                                </div>
                              )}
                              {group.items.map((sub) =>
                                sub.children ? (
                                  <div key={sub.label}>
                                    <button
                                      onClick={() => setMobileSubExpanded(mobileSubExpanded === sub.label ? null : sub.label)}
                                      className="w-full flex items-center gap-3 px-8 py-3 hover:bg-zinc-900 transition-colors"
                                    >
                                      <div className="text-zinc-500 flex-shrink-0">{sub.icon}</div>
                                      <div className="flex-1 text-left">
                                        <span className="text-zinc-300 text-sm font-[family-name:var(--font-saira)]">
                                          {sub.label}
                                        </span>
                                      </div>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                        className={`text-zinc-500 transition-transform duration-200 ${mobileSubExpanded === sub.label ? 'rotate-90' : ''}`}>
                                        <polyline points="9 18 15 12 9 6" />
                                      </svg>
                                    </button>
                                    {mobileSubExpanded === sub.label && (
                                      <div className="bg-zinc-900 border-l-2 border-zinc-700 ml-8">
                                        {sub.children.map((child) => (
                                          <Link
                                            key={child.href}
                                            href={child.href}
                                            onClick={() => setMobileOpen(false)}
                                            className="flex flex-col px-6 py-2.5 hover:bg-zinc-800 transition-colors"
                                          >
                                            <span className="text-zinc-300 text-sm font-[family-name:var(--font-saira)]">
                                              {child.label}
                                            </span>
                                            <span className="text-zinc-500 text-xs font-[family-name:var(--font-saira)]">
                                              {child.desc}
                                            </span>
                                          </Link>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <Link
                                    key={sub.href}
                                    href={sub.href ?? '#'}
                                    {...(sub.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center gap-3 px-8 py-3 hover:bg-zinc-900 transition-colors"
                                  >
                                    <div className="text-zinc-500 flex-shrink-0">{sub.icon}</div>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-zinc-300 text-sm font-[family-name:var(--font-saira)]">
                                          {sub.label}
                                        </span>
                                        {sub.soon && (
                                          <span className="bg-zinc-800 text-zinc-600 text-[8px] uppercase tracking-widest px-1.5 py-px font-[family-name:var(--font-saira)]">
                                            Soon
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-zinc-500 text-sm font-[family-name:var(--font-saira)]">
                                        {sub.desc}
                                      </div>
                                    </div>
                                  </Link>
                                )
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      href={item.href!}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 text-gray-100 hover:text-green-500 transition-colors text-lg font-[family-name:var(--font-saira)] px-8 py-3"
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              )
            })}
            <div className="px-8 py-5 flex flex-col gap-3">
              <Link
                href="/prop-firm"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center border border-green-500 text-green-500 hover:bg-green-500 hover:text-black text-[11px] font-bold uppercase tracking-widest py-3 transition-all font-[family-name:var(--font-saira)]"
              >
                Start trading
              </Link>
              <Link
                href="https://trade.crypto.st/en/sign-in"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center text-zinc-400 hover:text-white text-[9px] uppercase tracking-widest font-bold py-2 transition-colors font-[family-name:var(--font-saira)]"
              >
                Trader login
              </Link>
            </div>
          </nav>
        )}
      </header>
    </div>
  )
}